import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'redux'
import styled from 'styled-components'
import { Button, Checkbox, Input } from 'antd'
import { themeColor, fieldRequiredColor3, black } from '@edulastic/colors'
import ScreenReaderOnly from '../../../common/components/ScreenReaderOnly'
import {
  sendMfaCodeAction,
  verifyMfaCodeAction,
  abandonMfaSessionAction,
  setMfaVerifyErrorAction,
  getMfaState,
} from '../ducks'
import { getMfaEmailSendFailedAlertMessage } from '../mfaErrorMessages'
import { MFA_SEGMENT_ACTIONS, trackMfaEvent } from '../mfaSegmentTracking'

const MFA_CODE_INPUT_ID = 'mfa-code-input'
const MFA_HEADING_ID = 'mfa-verification-heading'
const MFA_CODE_LABEL_ID = 'mfa-code-label'
const MFA_EXPIRY_NOTE_ID = 'mfa-expiry-note'

const RESEND_COOLDOWN_SEC = 60

const MfaVerification = ({
  mfa,
  sendMfaCode,
  verifyMfaCode,
  abandonMfaSession,
  setMfaVerifyError,
  loadingComponents,
}) => {
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const emailSendFailed = Boolean(mfa?.show) && !mfa?.codeSent
  const emailSendFailedMessage = getMfaEmailSendFailedAlertMessage(
    mfa?.sendError
  )
  const emailSendFailedTitle =
    mfa?.sendError === 'user_email_not_found'
      ? 'No email on your account'
      : 'Verification code not sent'
  const isVerifying = loadingComponents.includes('mfaVerifyButton')
  const isResending = loadingComponents.includes('mfaResendButton')
  const isResendDisabled =
    isResending || (!emailSendFailed && resendCooldown > 0)
  const hasCodeError = Boolean(mfa?.verifyError)
  let resendLabel = 'Resend'
  if (emailSendFailed) {
    resendLabel = 'Send code'
  } else if (resendCooldown > 0) {
    resendLabel = `Resend (${resendCooldown}s)`
  }

  const codeDescribedBy = [
    MFA_EXPIRY_NOTE_ID,
    hasCodeError ? 'mfa-code-error' : null,
  ]
    .filter(Boolean)
    .join(' ')

  const resendAriaLabel = emailSendFailed
    ? 'Send verification code to your email'
    : resendCooldown > 0
    ? `Resend verification code, available in ${resendCooldown} seconds`
    : 'Resend verification code'

  useEffect(() => {
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 0 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (mfa?.codeSentAt) {
      setResendCooldown(RESEND_COOLDOWN_SEC)
    }
  }, [mfa?.codeSentAt])

  const handleVerify = () => {
    if (code.length !== 6) {
      return
    }
    trackMfaEvent(MFA_SEGMENT_ACTIONS.VERIFY)
    verifyMfaCode({
      code,
      trustDevice,
    })
  }

  const handleResend = () => {
    if (isResendDisabled) {
      return
    }
    setMfaVerifyError(false)
    trackMfaEvent(MFA_SEGMENT_ACTIONS.RESEND)
    sendMfaCode({ source: 'resend' })
  }

  const handleCodeChange = (e) => {
    if (hasCodeError) {
      setMfaVerifyError(false)
    }
    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
  }

  const handleSignOut = () => {
    trackMfaEvent(MFA_SEGMENT_ACTIONS.SIGNOUT)
    abandonMfaSession()
  }

  return (
    <MfaWrapper>
      <section aria-labelledby={MFA_HEADING_ID}>
        <Title id={MFA_HEADING_ID}>
          {emailSendFailed
            ? emailSendFailedTitle
            : 'Enter Your Verification Code'}
        </Title>
        {emailSendFailed ? (
          <EmailFailedAlert
            data-cy="mfaEmailSendFailed"
            data-send-error={mfa?.sendError || ''}
            role="alert"
            aria-live="assertive"
          >
            {emailSendFailedMessage}{' '}
          </EmailFailedAlert>
        ) : (
          <Subtitle id="mfa-verification-instructions">
            We sent a 6-digit code to {mfa?.maskedEmail || 'your email'}
          </Subtitle>
        )}
        <SignOutRow>
          Not You?{' '}
          <SignOutLink
            type="button"
            onClick={handleSignOut}
            aria-label="Sign out and return to login"
          >
            Sign out
          </SignOutLink>
        </SignOutRow>

        <CodeField>
          <ScreenReaderOnly id={MFA_CODE_LABEL_ID}>
            Six-digit verification code
          </ScreenReaderOnly>
          <CodeRow>
            <CodeInput
              id={MFA_CODE_INPUT_ID}
              data-cy="mfaCodeInput"
              autoFocus
              $hasError={hasCodeError}
              value={code}
              onChange={handleCodeChange}
              placeholder="------"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              onPressEnter={handleVerify}
              aria-labelledby={`${MFA_HEADING_ID} ${MFA_CODE_LABEL_ID}`}
              aria-describedby={codeDescribedBy || undefined}
              aria-invalid={hasCodeError}
              aria-required="true"
            />
            <ResendLink
              type="button"
              onClick={handleResend}
              disabled={isResendDisabled}
              aria-label={resendAriaLabel}
              aria-busy={isResending}
              data-cy="mfaResendButton"
            >
              {resendLabel}
            </ResendLink>
          </CodeRow>
          {hasCodeError && (
            <CodeError id="mfa-code-error" role="alert" aria-live="assertive">
              Invalid verification code. Please try again.
            </CodeError>
          )}
        </CodeField>
        <ExpiryNote id={MFA_EXPIRY_NOTE_ID}>
          {emailSendFailed
            ? 'Your sign-in session is active for 10 minutes.'
            : 'Code expires in 10 minutes'}
        </ExpiryNote>
        <TrustDeviceRow>
          <Checkbox
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
          >
            Trust this device for 30 days.
          </Checkbox>
        </TrustDeviceRow>

        <VerifyButton
          data-cy="mfaVerifyButton"
          type="primary"
          block
          loading={isVerifying}
          onClick={handleVerify}
          disabled={code.length !== 6}
          aria-busy={isVerifying}
          aria-disabled={code.length !== 6 || isVerifying}
        >
          Verify
        </VerifyButton>
      </section>
      <FooterNote>
        Can&apos;t access this email? Contact your IT Admin.
      </FooterNote>
      <FooterNote>
        For other issues,{' '}
        <SupportLink
          href="https://docs.goguardian.com/support/contact-support"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Contact support (opens in a new tab)"
        >
          contact support
        </SupportLink>
        .
      </FooterNote>
    </MfaWrapper>
  )
}

MfaVerification.propTypes = {
  mfa: PropTypes.object,
  sendMfaCode: PropTypes.func.isRequired,
  verifyMfaCode: PropTypes.func.isRequired,
  abandonMfaSession: PropTypes.func.isRequired,
  setMfaVerifyError: PropTypes.func.isRequired,
  loadingComponents: PropTypes.array,
}

MfaVerification.defaultProps = {
  mfa: {},
  loadingComponents: [],
}

const enhance = compose(
  connect(
    (state) => ({
      mfa: getMfaState(state),
      loadingComponents: state?.authorUi?.currentlyLoading || [],
    }),
    {
      sendMfaCode: sendMfaCodeAction,
      verifyMfaCode: verifyMfaCodeAction,
      abandonMfaSession: abandonMfaSessionAction,
      setMfaVerifyError: setMfaVerifyErrorAction,
    }
  )
)

export default enhance(MfaVerification)

const MfaWrapper = styled.div`
  width: 100%;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  line-height: 31.2px;
  color: ${black};
  margin: 0 0 16px;
  text-align: left;
`

const Subtitle = styled.p`
  color: ${black};
  margin: 0 0 16px;
  font-size: 18px;
  line-height: 130%;
  text-align: left;
`

const SignOutRow = styled.p`
  color: ${black};
  font-size: 18px;
  line-height: 130%;
  margin: 0 0 24px;
  text-align: left;
`

const focusVisibleRing = `
  &:focus-visible {
    outline: 2px solid ${themeColor};
    outline-offset: 2px;
  }
`

const SignOutLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${themeColor};
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 4px;
  &:hover {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
  }
  ${focusVisibleRing}
`

const CodeField = styled.div`
  margin-bottom: 8px;
`

const CodeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 8px;
`

const CodeInput = styled(Input)`
  flex: 1;
  height: 46px;
  font-size: 18px;
  letter-spacing: 8px;
  text-align: left;
  padding-left: 24px;
  padding-right: 24px;
  border-radius: 32px;
  border: 1px solid
    ${({ $hasError }) => ($hasError ? fieldRequiredColor3 : '#d8d8d8')};

  &:hover,
  &:focus {
    border-color: ${({ $hasError }) =>
      $hasError ? fieldRequiredColor3 : '#1fb58b'};
    box-shadow: ${({ $hasError }) =>
      $hasError ? `0 0 0 2px rgba(255, 77, 79, 0.2)` : 'none'};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ $hasError }) => ($hasError ? fieldRequiredColor3 : themeColor)};
    outline-offset: 2px;
  }
`

const CodeError = styled.p`
  color: ${fieldRequiredColor3};
  font-size: 14px;
  line-height: 16px;
  margin: 6px 0 0;
  text-align: left;
`

const EmailFailedAlert = styled.p`
  color: ${fieldRequiredColor3};
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 16px;
  text-align: left;
`

const ResendLink = styled.button`
  background: none;
  border: none;
  color: ${themeColor};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  ${focusVisibleRing}
`

const ExpiryNote = styled.p`
  color: #555555;
  font-size: 16px;
  line-height: 130%;
  font-weight: 500;
  margin: 8px 0 20px;
  text-align: left;
`

const TrustDeviceRow = styled.div`
  margin-bottom: 32px;

  .ant-checkbox-wrapper {
    font-size: 14px;
    color: #555555;
  }
`

const VerifyButton = styled(Button)`
  height: 46px;
  min-height: 46px;
  border-radius: 30px;
  padding: 0 28px;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 16px;
  background: ${themeColor};
  border-color: ${themeColor};

  &:hover,
  &:focus {
    background: ${themeColor};
    border-color: ${themeColor};
  }

  &:focus-visible {
    outline: 2px solid ${black};
    outline-offset: 2px;
  }
`

const FooterNote = styled.p`
  color: #595959;
  font-size: 14px;
  line-height: 16px;
  margin: 8px 0 12px;
`

const SupportLink = styled.a`
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  color: #595959;
  &:hover {
    color: #595959;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
  }
  ${focusVisibleRing}
`
